import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useState } from "react";

import PosLayout from "../../components/layouts/posLayout.comp";
import {
  listWarehouseTransferNotes,
  transferNoteIdString,
  WarehouseTransferListScope,
} from "../../core/warehouseTransferNote.core";
import { useTranslation } from "../../core/translation.core";
import useGetRetailShopId from "../../hooks/useGetRetailShopId.hook";

export default function WarehouseTransferListPage() {
  const { t, msg } = useTranslation();
  return (
    <PosLayout title={t(msg.warehouseTransfer.title)} showback>
      <Content />
    </PosLayout>
  );
}

function statusLabel(key: string, t: (x: any) => string, msg: any): string {
  switch (key) {
    case "draft":
      return t(msg.warehouseTransfer.statusDraft);
    case "submitted":
      return t(msg.warehouseTransfer.statusSubmitted);
    case "picking":
      return t(msg.warehouseTransfer.statusPicking);
    case "onDelivery":
      return t(msg.warehouseTransfer.statusOnDelivery);
    case "stockInCompleted":
      return t(msg.warehouseTransfer.statusStockInCompleted);
    default:
      return key;
  }
}

function Content() {
  const { t, msg } = useTranslation();
  const retailId = useGetRetailShopId();
  const [scope, setScope] = useState<WarehouseTransferListScope>("outgoing");

  const { data, isPending, isError, error } = useQuery({
    enabled: !!retailId,
    queryKey: ["warehouseTransferList", retailId, scope],
    queryFn: () => listWarehouseTransferNotes({ scope, paging: { page: 1, size: 100 } }),
  });

  return (
    <main className="dark:text-white space-y-6 p-2 lg:p-4 max-w-4xl" data-role="warehouse-transfer-list-page">
      <section data-type="section" className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight">{t(msg.warehouseTransfer.title)}</h1>
          <Link
            href="/warehouseTransfer/new"
            className="rounded-lg bg-her-blue text-white px-4 py-2 font-medium text-center shrink-0"
            data-role="warehouse-transfer-new-link"
          >
            {t(msg.warehouseTransfer.newTransfer)}
          </Link>
        </div>
        <div className="flex gap-2 border-t border-gray-100 dark:border-gray-700 pt-4">
          <button
            type="button"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              scope === "outgoing"
                ? "bg-her-blue text-white"
                : "bg-her-lightgray/80 dark:bg-her-darkblue2 text-gray-800 dark:text-gray-200"
            }`}
            onClick={() => setScope("outgoing")}
            data-role="warehouse-transfer-tab-outgoing"
          >
            {t(msg.warehouseTransfer.outgoing)}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              scope === "incoming"
                ? "bg-her-blue text-white"
                : "bg-her-lightgray/80 dark:bg-her-darkblue2 text-gray-800 dark:text-gray-200"
            }`}
            onClick={() => setScope("incoming")}
            data-role="warehouse-transfer-tab-incoming"
          >
            {t(msg.warehouseTransfer.incoming)}
          </button>
        </div>
      </section>

      <section data-type="section" className="space-y-4">
        {isPending && <p className="text-sm opacity-70">{t(msg.form.loading)}</p>}

        {!isPending && data && data.list.length === 0 && (
          <p className="text-sm opacity-80">{t(msg.warehouseTransfer.noTransfers)}</p>
        )}

        {!isPending && data && data.list.length > 0 && (
          <ul className="space-y-2" data-role="warehouse-transfer-list">
          {data.list.map((row) => (
            <li key={transferNoteIdString(row.id)}>
              <Link
                href={`/warehouseTransfer/${transferNoteIdString(row.id)}`}
                className="block rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-her-darkblue2 p-4 hover:border-her-blue"
                data-role={`warehouse-transfer-row-${transferNoteIdString(row.id)}`}
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-semibold">{row.transferNumber}</span>
                  <span className="text-sm rounded-full bg-her-lightgray dark:bg-her-bluegray px-3 py-0.5">
                    {statusLabel(row.status, t, msg)}
                  </span>
                </div>
                <p className="text-xs opacity-70 mt-1">
                  {row.items.length} {t(msg.product.product)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
        )}
      </section>
    </main>
  );
}
